<template>
  <div
    :class="statusBorderCss"
    class="border border-solid border-l-{10px} border-{#D9D9D9} bg-whiteOverride rounded-md transition-all duration-200 cursor-default overflow-hidden mb-{25px} hover:shadow-appComponent hover:bg-envelopeOverviewHover">
    <div class="flex flex-col">
      <div class="flex flex-row items-start p-{25px} md:p-none">
        <div class="flex flex-grow">
          <div class="hidden md:block pt-{25px} pb-{25px} pl-{25px}">
            <RouterLink
              :to="{
                name: 'EnvelopeStatus',
                params: { envelopeFingerprint: envelope.fingerprint },
              }">
              <AtomImage
                class="border border-solid rounded-md border-border w-cardThumbnailW h-cardThumbnailH"
                :src="envelope.thumbnail"
                :error="require('@/assets/error.png')"
                :alt="$t('envelope.documentThumbnail')"
                data-hj-suppress
              />
            </RouterLink>
          </div>
          <div class="flex-grow md:p-{25px}">
            <div class="flex flex-row h-full md:flex-col">
              <div class="flex-grow flex flex-col pr-{25px} md:pr-none">
                <RouterLink
                  :to="{
                    name: 'EnvelopeStatus',
                    params: { envelopeFingerprint: envelope.fingerprint },
                  }"
                  class="no-underline group">
                  <div
                    class="font-normal text-black break-all text-20 group-hover:underline">
                    {{ envelope.title }}
                  </div>
                  <div class="text-14 text-primaryGrey mt-sm font-normal">
                    {{ $t('envelope.created') }}
                    {{ envelope.created_at | formatDate('calendar') }}
                  </div>
                </RouterLink>
              </div>
              <div class="flex flex-col flex-wrap md:flex-row md:mt-{25px}">
                <BaseButton
                  v-if="envelope.status === 'draft'"
                  size="small"
                  class="card-button"
                  variant="tertiary"
                  @click.native="() => navigateTo('EnvelopeCreate')"
                  data-test="button-continue-creating"
                  :data-ec="ec"
                  data-et="envelope_continue_draft">
                  {{ $t('functional.edit') }}
                </BaseButton>
                <BaseButton
                  v-if="showCancel"
                  size="small"
                  variant="tertiary"
                  class="card-button"
                  @click.native="cancel"
                  :data-ec="ec"
                  data-et="envelope_cancel">
                  {{ $t('functional.cancel') }}
                </BaseButton>
                <DownloadFileButton
                  v-if="showDownloadButton"
                  :params="{ fingerprint: envelope.fingerprint }"
                  :file-name="envelope.title"
                  url="download/envelope"
                  size="small"
                  :displayIcon="false"
                  class="card-button"
                  variant="tertiary"
                  :data-ec="ec"
                  data-et="envelope_download"
                />
                <BaseButton
                  size="small"
                  v-if="showRemove"
                  @click.native="remove"
                  :data-ec="ec"
                  data-et="envelope_remove"
                  class="card-button"
                  variant="tertiary">
                  {{ $t('functional.delete') }}
                </BaseButton>
                <BaseButton
                  v-if="showRemind"
                  size="small"
                  class="card-button"
                  variant="tertiary"
                  @click.native="openResendNow"
                  :data-ec="ec"
                  data-et="envelope_resend_now">
                  {{ $t('envelope.resend') }}
                </BaseButton>
                <SendWidgetEmail
                  v-if="showVerify"
                  variant="tertiary"
                  size="small"
                  class="card-button"
                  :data-ec="ec"
                  data-et="envelope_verify"
                  :envelope="envelope"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="w-{230px} 2xl:w-{300px} min-w-{230px} 2xl:min-w-{300px} p-{25px} hidden md:block border-1 border-border border-solid border-t-0 border-r-0 border-b-0 self-stretch">
          <EnvelopeStatusPill
            :text="statusText"
            :variant="statusPillVariant" />
          <div class="mt-sm max-h-{300px} overflow-auto">
            <div
              v-for="party in envelope.parties"
              :key="party.id"
              class="flex flex-col mt-sm">
              <div
                :title="party.title"
                class="font-bold text-black truncate text-14"
                data-hj-suppress>
                {{ party.title }}
              </div>
              <div class="mt-{2px} text-14 font-normal text-primaryGrey">
                {{ formattedPartyStatus(party.status.computed_status, party.status.computed_status_date) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-offWhite py-base px-{25px} md:hidden flex flex-row">
        <EnvelopeStatusPill
          :text="statusText"
          :variant="statusPillVariant"
        />
      </div>
    </div>
  </div>
</template>

<script>
import EnvelopeStatusPill from '@/components/ui/envelopes/EnvelopeStatusPill.vue';
import SendWidgetEmail from '@/views/components/ResendWidgetEmailVerificationButton.vue';
import AtomImage from '@/components/atoms/Image.vue';
import DownloadFileButton from '@/components/DownloadFileButton.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';

import localeDateFormatter from '@/utils/locale-date-formatter';

export default {
  name: 'EnvelopeOverviewCard',
  components: {
    EnvelopeStatusPill,
    BaseButton,
    DownloadFileButton,
    SendWidgetEmail,
    AtomImage,
  },
  props: {
    envelope: {
      type: Object,
      required: true,
    },
    ec: {
      type: String,
      required: false,
      default: 'envelope_overview',
    },
  },
  data() {
    return {
      controlEnvelope: null,
      active: false,
      reminders: {
        loading: false,
        radio: null,
        custom: null,
        days: null,
      },
    };
  },
  methods: {
    /**
     * Get formatted party status string
     * Party status can only be one of: signed, bounced, sent, rejected, verify, queued, unsignedAwaiting or unsent
     */
    formattedPartyStatus(status, statusDate) {
      return this.$t(`envelope.partyStatus.${status}`, { date: localeDateFormatter('calendar', statusDate) });
    },
    openResendNow(event) {
      event.stopPropagation();
      this.$emit('openResendNowModal', this.envelope);
    },
    cancel(event) {
      event.stopPropagation();
      this.$emit('cancel');
    },
    remove(event) {
      event.stopPropagation();
      this.$emit('remove');
    },
    navigateTo(destination) {
      this.$router.push({ name: destination, params: { envelopeFingerprint: this.envelope.fingerprint } });
    },
  },
  computed: {
    statusPillVariant() {
      return {
        sent: 'inProgress',
        draft: 'draft',
        signed: 'signed',
        rejected: 'rejected',
        verify: 'unverified',
        expired: 'expired',
        cancelled: 'cancelled',
      }[this.envelope.status];
    },
    statusText() {
      return this.$t(`envelope.status.${this.envelope.status}`);
    },
    statusBorderCss() {
      return {
        sent: 'border-l-envelopeStatus-sent',
        draft: 'border-l-envelopeStatus-draft',
        signed: 'border-l-envelopeStatus-signed',
        rejected: 'border-l-envelopeStatus-rejected',
        verify: 'border-l-envelopeStatus-unverified',
        expired: 'border-l-envelopeStatus-expired',
        cancelled: 'border-l-envelopeStatus-cancelled',
      }[this.envelope.status];
    },
    remindDays() {
      if (this.envelope.auto_remind_hours > 0) {
        return this.envelope.auto_remind_hours / 24;
      }
      return null;
    },
    showRemove() {
      const removeableStatuses = [
        'signed',
        'rejected',
        'draft',
        'cancelled',
        'expired',
      ];
      return removeableStatuses.includes(this.envelope.status);
    },
    showRemind() {
      return this.envelope.status === 'sent';
    },
    showCancel() {
      return this.envelope.status === 'sent';
    },
    showVerify() {
      return this.envelope.status === 'verify';
    },
    showDownloadButton() {
      return this.envelope && ['signed', 'sent', 'cancelled', 'expired'].indexOf(this.envelope.status) > -1;
    },
  },
};
</script>

<style scoped>
.card-button {
  @apply
    whitespace-nowrap
    mb-xs
    md:mt-xs
    md:mb-0
    md:mr-xs
}
.card-button:last-child {
  @apply
    mb-0
    mr-0
}
</style>
